import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider, css } from 'styled-components';
import { inject, observer } from 'mobx-react';

import { toVw, transitionDefault } from '../utils/styles';
import { invert } from '../utils/theme';
import { isProduction } from '../utils';
import mq from '@/utils/media-queries';
import Footer from '../components/Footer';
import AnimationOnPageLoadGroup from '../components/AnimationOnPageLoadGroup';

export const SceneAnimationContext = React.createContext({});

const Styled = styled.div`
  position: relative;
  padding-top: ${toVw(178)};
  overflow: hidden;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  background-color: ${props => props.theme.colorBackground};
  color: ${props => props.theme.colorText};

  ${mq.mobile(css`
    padding-top: ${120}px;
  `)};
`;

const Inner = styled.div`
  ${isProduction() &&
    css`
      opacity: 0;
      ${transitionDefault};
    `};

  ${props =>
    props.pageLoaded &&
    css`
      opacity: 1;
    `};
`;

class Scene extends React.Component {
  static propTypes = {
    themeStore: PropTypes.object.isRequired,
    dark: PropTypes.bool,
    withTransition: PropTypes.bool,
    animationRunning: PropTypes.bool,
    shouldHandleColors: PropTypes.bool,
    pageLoaded: PropTypes.bool.isRequired,
    brightFooter: PropTypes.bool,
    preloaderStore: PropTypes.object.isRequired,
    onAnimationEnded: PropTypes.func,
  };

  state = {
    animationContext: {
      shouldRunContentAnimation: false,
    },
  };

  componentDidMount() {
    const { dark, withTransition } = this.props;

    if (!withTransition) {
      this.handleColor(dark);
    }

    if (!this.props.animationRunning) {
      if (isProduction() && !this.props.preloaderStore.loaded) {
        this.props.preloaderStore.addListener(() => this.startInitialAnimation());
      } else {
        this.startInitialAnimation();
      }

      this.animationEnded();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { dark, withTransition } = this.props;

    if (nextProps.dark !== dark) {
      if (!withTransition) {
        this.handleColor(nextProps.dark);
      }
    }

    if (this.props.shouldHandleColors !== nextProps.shouldHandleColors) {
      if (withTransition && nextProps.shouldHandleColors) {
        this.handleColor(nextProps.dark);
      }
    }

    if (nextProps.animationRunning !== this.props.animationRunning) {
      if (!nextProps.animationRunning) {
        this.animateContent();
        this.animationEnded();
      }
    }
  }

  animationEnded() {
    if (this.props.onAnimationEnded) {
      this.props.onAnimationEnded();
    }
  }

  startInitialAnimation() {
    setTimeout(() => this.animateContent(), 100);
  }

  handleColor(isDark) {
    if (isDark) {
      this.props.themeStore.setDark();
    } else {
      this.props.themeStore.setBright();
    }
  }

  animateContent() {
    this.setState({
      animationContext: {
        shouldRunContentAnimation: true,
      },
    });
  }

  render() {
    const { className, children, dark, pageLoaded, brightFooter, ...rest } = this.props;

    return (
      <ThemeProvider theme={dark ? invert : {}}>
        <SceneAnimationContext.Provider value={this.state.animationContext}>
          <AnimationOnPageLoadGroup>
            <Styled className={className} {...rest} data-scene>
              <Inner pageLoaded={pageLoaded} innerRef={node => (this.innerNode = node)}>
                {children}
              </Inner>
              <Footer bright={brightFooter} inScene />
            </Styled>
          </AnimationOnPageLoadGroup>
        </SceneAnimationContext.Provider>
      </ThemeProvider>
    );
  }
}

export default inject(stores => ({
  themeStore: stores.store.theme,
  preloaderStore: stores.store.preloaderStore,
  sceneChangerStore: stores.store.sceneChanger,
}))(
  observer(({ sceneChangerStore, preloaderStore, ...rest }) => (
    <Scene
      preloaderStore={preloaderStore}
      pageLoaded={preloaderStore.loaded}
      shouldHandleColors={sceneChangerStore.shouldHandleColors}
      {...rest}
    />
  ))
);



// WEBPACK FOOTER //
// ./src/scenes/Scene.js